import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Alert from '../components/common/Alert';
import BrandMark from '../components/common/BrandMark';
import Button from '../components/common/Button';
import Input from '../components/forms/Input';
import { useAuth } from '../context/AuthContext';

function ChangePasswordPage() {
  const { changePassword, user } = useAuth();
  const navigate = useNavigate();
  const [requestErrors, setRequestErrors] = useState([]);
  const [show, setShow] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm();
  const newPassword = watch('new_password');

  const submit = async (values) => {
    setRequestErrors([]);
    try {
      await changePassword(values);
      navigate(user.role === 'VOTANTE' ? '/mi-perfil' : '/dashboard', {
        replace: true,
      });
    } catch (requestError) {
      setRequestErrors(getErrorMessages(requestError));
    }
  };

  const visibilityButton = (
    <button
      type="button"
      onClick={() => setShow((value) => !value)}
      className="grid size-8 place-items-center rounded-md text-content-muted hover:bg-slate-100"
      aria-label={show ? 'Ocultar contraseñas' : 'Mostrar contraseñas'}
    >
      {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
    </button>
  );

  return (
    <main className="grid min-h-screen place-items-center bg-surface-page px-5 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 inline-flex rounded-xl bg-slate-950 p-3"><BrandMark /></div>
        <div className="rounded-card border border-border bg-white p-6 shadow-card sm:p-8">
          <span className="grid size-11 place-items-center rounded-xl bg-primary-50 text-primary-700">
            <KeyRound className="size-5" />
          </span>
          <h1 className="mt-5 text-2xl font-semibold text-content-primary">Cambia tu contraseña</h1>
          <p className="mt-2 text-sm leading-6 text-content-muted">
            Por seguridad debes reemplazar la contraseña inicial antes de acceder a Votum.
          </p>
          {requestErrors.length > 0 && (
            <Alert variant="danger" title="Revisa la contraseña" className="mt-5">
              <ul className="list-disc space-y-1 pl-5">
                {requestErrors.map((message) => <li key={message}>{message}</li>)}
              </ul>
            </Alert>
          )}
          <form className="mt-6 space-y-4" onSubmit={handleSubmit(submit)} noValidate>
            <Input
              id="current-password"
              type={show ? 'text' : 'password'}
              label="Contraseña actual"
              autoComplete="current-password"
              required
              endAdornment={visibilityButton}
              error={errors.current_password?.message}
              {...register('current_password', { required: 'Ingresa tu contraseña actual.' })}
            />
            <Input
              id="new-password"
              type={show ? 'text' : 'password'}
              label="Contraseña nueva"
              autoComplete="new-password"
              required
              error={errors.new_password?.message}
              hint="Mínimo 8 caracteres; evita claves comunes o solo numéricas."
              {...register('new_password', {
                required: 'Ingresa una contraseña nueva.',
                minLength: { value: 8, message: 'Mínimo 8 caracteres.' },
                validate: {
                  notDocument: (value) =>
                    normalizeComparable(value) !== user.cedula ||
                    'La contraseña no puede ser tu número de cédula.',
                  noSequence: (value) =>
                    !containsConsecutiveDigits(value) ||
                    'La contraseña no puede contener cuatro o más números consecutivos.',
                },
              })}
            />
            <Input
              id="confirm-password"
              type={show ? 'text' : 'password'}
              label="Confirmar contraseña nueva"
              autoComplete="new-password"
              required
              error={errors.confirm_password?.message}
              {...register('confirm_password', {
                required: 'Confirma la contraseña nueva.',
                validate: (value) => value === newPassword || 'Las contraseñas no coinciden.',
              })}
            />
            <Button type="submit" size="lg" className="w-full" loading={isSubmitting}>
              Guardar contraseña
            </Button>
          </form>
        </div>
      </div>
    </main>
  );
}

const normalizeComparable = (value) =>
  String(value || '').replace(/[\s.-]/g, '');

const containsConsecutiveDigits = (value) => {
  const runs = String(value || '').match(/\d{4,}/g) || [];
  return runs.some((run) => {
    for (let start = 0; start <= run.length - 4; start += 1) {
      const digits = run.slice(start, start + 4).split('').map(Number);
      const differences = digits.slice(1).map(
        (digit, index) => digit - digits[index]
      );
      if (differences.every((difference) => difference === 1) ||
          differences.every((difference) => difference === -1)) {
        return true;
      }
    }
    return false;
  });
};

const flattenMessages = (value) => {
  if (Array.isArray(value)) return value.flatMap(flattenMessages);
  if (value && typeof value === 'object') {
    return Object.values(value).flatMap(flattenMessages);
  }
  return typeof value === 'string' ? [value] : [];
};

const getErrorMessages = (error) => {
  const messages = flattenMessages(error.data?.errors);
  return [...new Set(messages.length ? messages : [error.message])];
};

export default ChangePasswordPage;
